import { useCallback, useEffect, useMemo, useState } from 'react';
import { deletePaciente, getPacientesActivos } from '../api/pacientesApi';
import { getGrupoName } from '../model/pacienteConstants';
import { formatDateOnly } from '../../../shared/utils/dateOnly';

const mapPacienteRow = (paciente) => ({
  id: paciente.id,
  codHistorico: paciente.codHistorico,
  nombre: `${paciente.firstname} ${paciente.lastname}`,
  dateBirth: formatDateOnly(paciente.dateBirth),
  grupo: paciente.grupo,
  grupoNombre: getGrupoName(paciente.grupo),
  sexo: paciente.sexo === 1 ? 'Hombre' : 'Mujer',
});

export const usePacientesTable = () => {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchName, setSearchName] = useState('');
  const [searchId, setSearchId] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterSexo, setFilterSexo] = useState('');

  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getPacientesActivos();
      setPatients(data.map(mapPacienteRow));
    } catch (error) {
      console.error('Error fetching patients:', error);
      setPatients([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  const filteredPatients = useMemo(() => (
    patients.filter((patient) => {
      const matchesId = searchId.trim() === '' || patient.id === parseInt(searchId, 10);
      const matchesName = searchName.trim() === '' || patient.nombre.toLowerCase().includes(searchName.toLowerCase());
      const matchesGroup = filterGroup === '' || patient.grupo === Number(filterGroup);
      const matchesSexo = filterSexo === '' || patient.sexo === filterSexo;
      return matchesId && matchesName && matchesGroup && matchesSexo;
    })
  ), [patients, searchId, searchName, filterGroup, filterSexo]);

  const resetFilters = () => {
    setSearchName('');
    setSearchId('');
    setFilterGroup('');
    setFilterSexo('');
  };

  const removePaciente = async (idPaciente) => {
    await deletePaciente(idPaciente);
    await fetchPatients();
  };

  return {
    loading,
    filteredPatients,
    searchName,
    setSearchName,
    searchId,
    setSearchId,
    filterGroup,
    setFilterGroup,
    filterSexo,
    setFilterSexo,
    resetFilters,
    fetchPatients,
    removePaciente,
  };
};
